import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';

const GenderSelect = ({
    value,
    onChange,
    options = [],
    placeholder,
    disabled = false,
    error = false,
    className = '',
    size = 'default', // 'default' | 'small'
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    const selectedOption = options?.find(o => o.value === value) || null;

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleSelect = (val) => {
        onChange(val);
        setIsOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
        setIsOpen(false);
    };

    const isSmall = size === 'small';

    return (
        <div data-testid="gender-select-container" ref={containerRef} className={`relative ${className}`}>
            {/* Trigger Button */}
            <button
                data-testid="gender-select-button"
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`w-full ${isSmall ? 'px-3 py-2' : 'px-4 py-3'} rounded-xl border text-sm font-medium transition-all duration-200 flex items-center justify-between gap-2 ${
                    error
                        ? 'border-red-500 bg-red-50'
                        : isOpen
                            ? 'border-[#4ABDBD] ring-2 ring-[#4ABDBD]/20 bg-white'
                            : 'border-gray-300 hover:border-[#4ABDBD] bg-white'
                } ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'cursor-pointer'}`}
            >
                <span data-testid="gender-select-value-text" className={`truncate ${!selectedOption ? 'text-gray-400' : 'text-gray-800'}`}>
                    {selectedOption ? selectedOption.label : (placeholder || t("common.select"))}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {value && !disabled && (
                        <span
                            data-testid="gender-select-clear-button"
                            onClick={handleClear}
                            className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <X data-testid="gender-select-clear-icon" className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                        </span>
                    )}
                    <ChevronDown data-testid="gender-select-chevron-icon" className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {/* Dropdown */}
            {isOpen && !disabled && (
                <div data-testid="gender-select-dropdown" className="absolute z-50 left-0 top-full mt-1 w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
                    <div className="max-h-48 overflow-y-auto custom-scrollbar">
                        {options.map((o) => {
                            const isSelected = value === o.value;
                            return (
                                <button
                                    data-testid={`gender-select-option-${o.value}`}
                                    key={o.value}
                                    type="button"
                                    onClick={() => handleSelect(o.value)}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-all duration-150 ${
                                        isSelected
                                            ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold'
                                            : 'text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <span className="truncate flex-1 text-left">{o.label}</span>
                                    {isSelected && (
                                        <Check data-testid={`gender-select-option-${o.value}-check-icon`} className="w-4 h-4 text-[#4ABDBD] flex-shrink-0" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GenderSelect;
